import React, { useState, useEffect } from 'react';
import { Restaurant } from '@/lib/supabase';
import { Button } from '@/components/ui';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';

interface ClientFormProps {
  client?: Restaurant | null;
  onSubmit: (data: Partial<Restaurant>) => void;
  onCancel: () => void;
  isLoading?: boolean;
}

export const ClientForm: React.FC<ClientFormProps> = ({
  client,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [formData, setFormData] = useState({
    name: client?.name || '',
    slug: client?.slug || '',
    contact_name: (client?.primary_contact as any)?.name || '',
    contact_role: (client?.primary_contact as any)?.role || '',
    description: client?.description || '',
    location: client?.location || '',
    website: client?.website || '',
    phone: client?.phone || '',
    email: client?.email || '',
    cuisine: client?.cuisine?.join(', ') || '',
    price_range: client?.price_range || '€€',
    established: client?.established?.toString() || '',
    pack: client?.pack || 'essentiel',
    status: client?.status || 'prospect',
    monthly_revenue: client?.monthly_revenue?.toString() || '',
    rcs: client?.rcs || '',
    siret: client?.siret || '',
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    const data: Partial<Restaurant> = {
      name: formData.name,
      slug: formData.slug,
      description: formData.description || null,
      location: formData.location || null,
      website: formData.website || null,
      phone: formData.phone || null,
      email: formData.email || null,
      cuisine: formData.cuisine ? formData.cuisine.split(',').map(c => c.trim()) : null,
      price_range: formData.price_range || null,
      established: formData.established ? parseInt(formData.established) : null,
      pack: formData.pack as any,
      status: formData.status as any,
      monthly_revenue: formData.monthly_revenue ? parseFloat(formData.monthly_revenue) : null,
      rcs: formData.rcs || null,
      siret: formData.siret || null,
      primary_contact: {
        name: formData.contact_name || null,
        role: formData.contact_role || null,
        email: formData.email || null,
        phone: formData.phone || null,
      } as any,
    };

    onSubmit(data);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        {/* Nom */}
        <div className="col-span-2">
          <Label htmlFor="name">Nom du restaurant *</Label>
          <Input
            id="name"
            value={formData.name}
            onChange={(e) => setFormData({ ...formData, name: e.target.value })}
            required
            placeholder="Le 39V"
          />
        </div>

        {/* Slug */}
        <div className="col-span-2">
          <Label htmlFor="slug">Slug (URL) *</Label>
          <Input
            id="slug"
            value={formData.slug}
            onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/\s+/g, '-') })}
            required
            placeholder="le-39v"
          />
          <p className="text-xs text-gray-500 mt-1">Utilisé dans les URLs</p>
        </div>

        {/* Contact Name */}
        <div>
          <Label htmlFor="contact_name">Nom du contact principal</Label>
          <Input
            id="contact_name"
            value={formData.contact_name}
            onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
            placeholder="Jean Dupont"
          />
          <p className="text-xs text-gray-500 mt-1">Personne de contact dans l'établissement</p>
        </div>

        {/* Contact Role */}
        <div>
          <Label htmlFor="contact_role">Fonction du contact</Label>
          <Input
            id="contact_role"
            value={formData.contact_role}
            onChange={(e) => setFormData({ ...formData, contact_role: e.target.value })}
            placeholder="Directeur Général"
          />
        </div>

        {/* Description */}
        <div className="col-span-2">
          <Label htmlFor="description">Description</Label>
          <textarea
            id="description"
            value={formData.description}
            onChange={(e) => setFormData({ ...formData, description: e.target.value })}
            className="w-full border border-gray-300 rounded-md p-2 text-sm min-h-[80px]"
            placeholder="Restaurant gastronomique..."
          />
        </div>

        {/* Location */}
        <div>
          <Label htmlFor="location">Localisation</Label>
          <Input
            id="location"
            value={formData.location}
            onChange={(e) => setFormData({ ...formData, location: e.target.value })}
            placeholder="Paris 8ème, France"
          />
        </div>

        {/* Établi en */}
        <div>
          <Label htmlFor="established">Établi en</Label>
          <Input
            id="established"
            type="number"
            value={formData.established}
            onChange={(e) => setFormData({ ...formData, established: e.target.value })}
            placeholder="2015"
          />
        </div>

        {/* Email */}
        <div>
          <Label htmlFor="email">Email</Label>
          <Input
            id="email"
            type="email"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
            placeholder="contact@restaurant.com"
          />
        </div>

        {/* Téléphone */}
        <div>
          <Label htmlFor="phone">Téléphone</Label>
          <Input
            id="phone"
            value={formData.phone}
            onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
            placeholder="+33 1 23 45 67 89"
          />
        </div>

        {/* Website */}
        <div className="col-span-2">
          <Label htmlFor="website">Site web</Label>
          <Input
            id="website"
            type="url"
            value={formData.website}
            onChange={(e) => setFormData({ ...formData, website: e.target.value })}
            placeholder="https://restaurant.com"
          />
        </div>

        {/* Cuisine */}
        <div className="col-span-2">
          <Label htmlFor="cuisine">Cuisine (séparés par des virgules)</Label>
          <Input
            id="cuisine"
            value={formData.cuisine}
            onChange={(e) => setFormData({ ...formData, cuisine: e.target.value })}
            placeholder="Française, Gastronomique, Moderne"
          />
        </div>

        {/* Price Range */}
        <div>
          <Label htmlFor="price_range">Gamme de prix</Label>
          <select
            id="price_range"
            value={formData.price_range}
            onChange={(e) => setFormData({ ...formData, price_range: e.target.value })}
            className="w-full border border-gray-300 rounded-md p-2 text-sm"
          >
            <option value="€">€ (Économique)</option>
            <option value="€€">€€ (Modéré)</option>
            <option value="€€€">€€€ (Cher)</option>
            <option value="€€€€">€€€€ (Très cher)</option>
          </select>
        </div>

        {/* Pack */}
        <div>
          <Label htmlFor="pack">Pack STRATOS</Label>
            <select
              id="pack"
              value={formData.pack}
              onChange={(e) => setFormData({ ...formData, pack: e.target.value as any })}
              className="w-full border border-gray-300 rounded-md p-2 text-sm"
            >
            <option value="">Aucun</option>
            <option value="essentiel">Essentiel (1000€/mois)</option>
            <option value="premium">Premium (2000€/mois)</option>
            <option value="signature">Signature (3500€/mois)</option>
          </select>
        </div>

        {/* Statut */}
        <div>
          <Label htmlFor="status">Statut</Label>
            <select
              id="status"
              value={formData.status}
              onChange={(e) => setFormData({ ...formData, status: e.target.value as any })}
              className="w-full border border-gray-300 rounded-md p-2 text-sm"
            >
            <option value="prospect">Prospect</option>
            <option value="active">Actif</option>
            <option value="paused">En pause</option>
            <option value="churned">Churn</option>
          </select>
        </div>

        {/* Revenue */}
        <div>
          <Label htmlFor="monthly_revenue">Revenu mensuel (€)</Label>
          <Input
            id="monthly_revenue"
            type="number"
            step="0.01"
            value={formData.monthly_revenue}
            onChange={(e) => setFormData({ ...formData, monthly_revenue: e.target.value })}
            placeholder="2000.00"
          />
        </div>

        {/* RCS */}
        <div>
          <Label htmlFor="rcs">Numéro RCS</Label>
          <Input
            id="rcs"
            value={formData.rcs}
            onChange={(e) => setFormData({ ...formData, rcs: e.target.value })}
            placeholder="Paris B 123 456 789"
          />
        </div>

        {/* SIRET */}
        <div>
          <Label htmlFor="siret">Numéro SIRET</Label>
          <Input
            id="siret"
            value={formData.siret}
            onChange={(e) => setFormData({ ...formData, siret: e.target.value })}
            placeholder="12345678900012"
            maxLength={14}
          />
          <p className="text-xs text-gray-500 mt-1">14 chiffres</p>
        </div>
      </div>

      {/* Buttons */}
      <div className="flex items-center justify-end gap-3 pt-4 border-t">
        <Button
          type="button"
          variant="outline"
          onClick={onCancel}
          disabled={isLoading}
        >
          Annuler
        </Button>
        <Button
          type="submit"
          disabled={isLoading}
          className="bg-stratos-gold hover:bg-stratos-gold-dark text-gray-900"
        >
          {isLoading ? 'Enregistrement...' : client ? 'Mettre à jour' : 'Créer'}
        </Button>
      </div>
    </form>
  );
};

